import Head from 'next/head';

import {useMetaData} from './useMetaData';

import type {PageData} from 'PayloadUI/dataSelectors/getPageData';

/**
 * The `ComponentProps` interface defines the expected properties for the `MetaData` component.
 * This interface encapsulates all the contextual and content-specific information required to generate dynamic metadata
 * for SEO, social sharing, and structured data. Each property contributes to enhancing page discoverability and visual presentation
 * in search engines and social platforms. The fields are optional to provide flexibility during integration.
 */
interface ComponentProps {
    pageMetaData?: PageData['pageMetaData'];
}

/**
 * Renders SEO and social media meta tags for a given page using `next/head`.
 * This component dynamically injects all relevant `<meta>`, `<title>`, and `<link rel="canonical">` tags
 * based on the provided `pageMetaData`. It ensures that Open Graph, Twitter Cards, and basic SEO tags
 * are always consistent with the current page's content.
 * Additionally, it includes schema.org JSON-LD structured data by rendering one or more `<script>` blocks
 * with type `"application/ld+json"`. These scripts include structured metadata like breadcrumb trails
 * and organization data to enhance search engine understanding and eligibility for rich results.
 * If optional fields such as `description`, `keywords`, or `image` are not provided, the corresponding
 * tags are omitted to prevent rendering empty or misleading metadata.
 *
 * @param props              The component props.
 * @param props.pageMetaData A `PageMetaData` object containing SEO fields, breadcrumb hierarchy, and tenant info.
 * @returns A JSX `<Head>` element with fully populated meta tags and JSON-LD schemas.
 *
 * @example
 * ```tsx
 * <MetaData pageMetaData={pageMetaData} />
 * ```
 */
const MetaData = ({pageMetaData}: ComponentProps) => {
    const {
        canonicalUrl,
        desc,
        image,
        keywords,
        title,
        url
    } = useMetaData(pageMetaData);

    return (
        <Head>
            <title>{title}</title>
            {desc && <meta content={desc} name="description" />}
            {keywords && <meta content={keywords} name="keywords" />}
            <meta content="index, follow" name="robots" />
            <meta content={title} property="og:title" />
            {desc && <meta content={desc} property="og:description" />}
            {image && <meta content={image} property="og:image" />}
            <meta content="website" property="og:type" />
            <meta content={url} property="og:url" />
            <meta content={title} property="og:site_name" />
            <meta content="summary_large_image" name="twitter:card" />
            <meta content={title} name="twitter:title" />
            {desc && <meta content={desc} name="twitter:description" />}
            {image && <meta content={image} name="twitter:image" />}
            {canonicalUrl && <link href={canonicalUrl} rel="canonical" />}
        </Head>
    );
};

MetaData.displayName = 'MetaData';

export {MetaData};